import { useCallback, useEffect, useState } from 'react';
import { Card, Divider, EmptyState, Icon, PaginationControls, SearchBar, Skeleton, TextButton } from '../../components';
import { KNOWLEDGE_HUB_TYPE_CONFIG, MAX_CARD_WIDTH, PAGINATION_PAGE_SIZE } from '../../constants';
import { useHandleApiResponse } from '../../hooks';
import { KnowledgeHubRecord, useGetKnowledgeHubMutation } from '../../services';
import { KnowledgeHubRecordType } from '../../types';
import { getKnowledgeHubIcon, kebabCaseToLabel } from '../../utils';
import KnowledgeHubItem, { KNOWLEDGE_HUB_ITEM_MIN_HEIGHT, KNOWLEDGE_HUB_NEW_RECORD_PREFIX } from './KnowledgeHubItem';

const CARD_GAP = 0;
const NUM_SKELETONS = 3;

interface KnowledgeHubPageProps {
  type: KnowledgeHubRecordType;
}

/**
 * Knowledge Hub page component that manages both draft and existing records.
 * Provides functionality to create, display, and manage knowledge hub items.
 */
const KnowledgeHubPage = ({ type }: KnowledgeHubPageProps) => {
  // Counter for generating unique draft record IDs that only increments.
  const [draftIdCounter, setDraftIdCounter] = useState(0);
  // Local state for managing draft records before they're saved.
  const [draftRecords, setDraftRecords] = useState<Pick<KnowledgeHubRecord, 'id' | 'content'>[]>([]);
  // Local state for managing existing records fetched from the server.
  const [existingRecords, setExistingRecords] = useState<KnowledgeHubRecord[]>([]);
  // Local state for managing pagination.
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');

  // Combine draft and existing records for display.
  const allRecords = [...draftRecords, ...existingRecords];

  const [getKnowledgeHub, { isLoading }] = useGetKnowledgeHubMutation();

  const handleApiResponse = useHandleApiResponse();

  const isEmpty = !isLoading && !allRecords.length;
  const showLoading = isLoading && !allRecords.length;

  const label = kebabCaseToLabel(KNOWLEDGE_HUB_TYPE_CONFIG[type].path).toLowerCase();

  const fetchKnowledgeHub = useCallback(async () => {
    const response = await getKnowledgeHub({
      page: page + 1, // PaginationControls uses 0-based page indexing, so we need to add 1 to the page index.
      perPage: PAGINATION_PAGE_SIZE,
      search,
      type,
    });
    handleApiResponse({
      response,
      hideErrorToast: true,
      hideSuccessToast: true,
      onSuccess: (data) => {
        // Update state with fetched data.
        setExistingRecords(data.records);
        setTotalPages(data.pagination.totalPages);
      },
    });
  }, [page, search, type, getKnowledgeHub, handleApiResponse]);

  // Creates a new draft record with a unique ID using an incrementing counter.
  const handleCreateDraft = useCallback(() => {
    setDraftRecords((prev) => [...prev, { id: `${KNOWLEDGE_HUB_NEW_RECORD_PREFIX}${draftIdCounter}`, content: '' }]);
    setDraftIdCounter((prev) => prev + 1);
  }, [draftIdCounter, setDraftRecords]);

  // Resets all state variables to their initial values when the knowledge hub type changes.
  // We intentionally clear any unsaved draft records.
  useEffect(() => {
    setDraftIdCounter(0);
    setDraftRecords([]);
    setExistingRecords([]);
    setPage(0);
    setSearch('');
    setTotalPages(0);
  }, [type]);

  // Fetches existing knowledge hub records.
  useEffect(() => {
    fetchKnowledgeHub();
  }, [fetchKnowledgeHub]);

  const renderItem = ({
    id,
    component,
    index,
    length,
  }: {
    id: string;
    component: React.ReactNode;
    index: number;
    length: number;
  }) => (
    <div key={id}>
      {component}
      {index < length - 1 && <Divider className="before:bg-base-300 after:bg-base-300" />}
    </div>
  );

  return (
    <div className="flex h-full flex-col items-end gap-4">
      <div className="flex items-center gap-2">
        <SearchBar
          value={search}
          onSubmit={(value) => {
            // Reset the page to the first page to avoid empty results if the user is on a higher page and the new search query yields fewer total pages.
            setPage(0);
            setSearch(value);
          }}
        />
        <TextButton text="Create" startIcon={Icon.PLUS} onClick={handleCreateDraft} />
      </div>
      {/* Empty state when no records exist. */}
      {isEmpty && (
        <EmptyState
          description={search ? `No ${label} found matching your search` : `No ${label} added yet`}
          icon={getKnowledgeHubIcon(type)}
        />
      )}
      {/* Records list. */}
      {!isEmpty && (
        <div className="flex h-full w-full flex-col items-center justify-between gap-4 overflow-hidden">
          <Card gap={CARD_GAP} maxWidth={MAX_CARD_WIDTH} className="py-2">
            {/* Loading state for initial page load. */}
            {showLoading &&
              Array.from({ length: NUM_SKELETONS }, (_, index) =>
                renderItem({
                  component: (
                    <div className="py-3">
                      <Skeleton size={KNOWLEDGE_HUB_ITEM_MIN_HEIGHT} />
                    </div>
                  ),
                  id: index.toString(),
                  index,
                  length: NUM_SKELETONS,
                })
              )}
            {!showLoading &&
              allRecords.map((record, index) =>
                renderItem({
                  component: (
                    <KnowledgeHubItem
                      currPage={page}
                      currPageSize={existingRecords.length}
                      initialValue={record.content}
                      recordId={record.id}
                      totalPages={totalPages}
                      type={type}
                      refetchKnowledgeHub={fetchKnowledgeHub}
                      setCurrPage={setPage}
                      setDraftRecords={setDraftRecords}
                      setExistingRecords={setExistingRecords}
                    />
                  ),
                  id: record.id,
                  index,
                  length: allRecords.length,
                })
              )}
          </Card>
          {totalPages > 1 && <PaginationControls currentPage={page} totalPages={totalPages} setCurrentPage={setPage} />}
        </div>
      )}
    </div>
  );
};

export default KnowledgeHubPage;
